import React, { useState, useEffect } from "react";
import classes from './PaymentVoucher.module.css';
import HeaderNav from '../../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../../Components/Footer/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../Components/API/Api";
import { useUser } from '../../../UserContext';
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';

const PaymentVoucher = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem("authToken");
    const [ledgerData, setLedgerData] = useState([]);
    const [loading, setLoading] = useState(true);
 const { user, isAuthenticated, logout } = useUser();
  
  // Debug: Log user data to see what's available
  console.log('DashboardHeader - User data:', user);
  console.log('DashboardHeader - Is authenticated:', isAuthenticated);
  
  // Get user's display name - fallback to 'User' if no name is available
  const displayName = user?.name || user?.firstName || user?.email?.split('@')[0] || 'User';
  
  console.log('DashboardHeader - Display name:', displayName);
    // new: store logged-in company info
    const [companyInfo, setCompanyInfo] = useState({
        name: "",
        address: "",
        email: "",
        phone: "",
        logo: ""
    });

    // NEW: base currency code & symbol (set from company settings)
    const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
    const [baseCurrencySymbol, setBaseCurrencySymbol] = useState(null);

    // NEW: read payment passed via navigate state — declare before any useEffect that references it
    const payment = location.state?.payment || null;

    const typeMap = {
        "1": "Assets",
        "2": "Liabilities",
        "3": "Equity",
        "4": "Revenue",
        "5": "Expenses"
    };

    const categoryMap = {
        "1": "Current Assets",
        "2": "Non-current Assets",
        "3": "Current Liabilities",
        "4": "Non-current Liabilities",
        "5": "Equity",
        "6": "Operating Revenue",
        "7": "Cost of Sales",
        "8": "Operating Expenses"
    };


    const formatMoney = (value) => {
        const num = Number(value || 0);
        return num.toLocaleString("en-NG", { minimumFractionDigits: 2 });
    };

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await api.get('/v1/chart_of_account');

                const accounts = Array.isArray(response?.data?.data?.data) ? response.data.data.data : [];
                const mappedData = accounts.map((account, index) => ({
                    id: account.id || `account-${index}`,
                    ref: account.gl_code || `REF-${index}`,
                    description: account.gl_name,
                    type: typeMap[account.class_id] || "Unknown",
                    category: categoryMap[account.category_id] || "Unknown",
                    balance: formatMoney(account.balance || 0),
                    status: account.status === "1" ? "Active" : "Inactive",
                }));


                setLedgerData(mappedData);
            } catch (error) {
                console.error("Error fetching chart of accounts:", error);
            } finally {
                setLoading(false);
            }
        };
        if (token) fetchAccounts();
        else setLoading(false);
    }, [token]);

    // new: fetch company info for header display
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            if (!token) return;
            try {
                const res = await api.get('/v1/companies/get-info');
                const item = Array.isArray(res?.data?.data) ? res.data.data[0] : (res?.data?.data || null);
                if (item) {
                    setCompanyInfo({
                        name: item.name || "",
                        address: item.address || "",
                        email: item.email || "",
                        phone: item.phone_number || "",
                        logo: item.logo || ""
                    });

                    // NEW: resolve base currency from company info if present
                    const currencyId = item?.currency_base;
                    if (currencyId) {
                            try {
                            const currenciesRes = await api.get('/v1/fetch-all-currencies');
                            const currencies = Array.isArray(currenciesRes?.data?.data) ? currenciesRes.data.data : (currenciesRes?.data?.data || []);
                            const baseCurrency = currencies.find(c => String(c.id) === String(currencyId));
                            if (baseCurrency) {
                                setBaseCurrencyCode(baseCurrency.code || null);
                                setBaseCurrencySymbol(baseCurrency.symbol || null);
                            }
                        } catch (curErr) {
                            console.error("Failed to fetch currencies for base currency", curErr);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
    }, [token]);

    // NEW: helper to title-case a name
    const titleCase = (s) => {
        if (!s || typeof s !== "string") return s;
        return s
            .split(" ")
            .map(p => p.trim())
            .filter(Boolean)
            .map(word => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    // NEW: helper to synchronously get stored user display name (localStorage / sessionStorage)
    const getStoredUserName = () => {
        // First try the exact key HeaderNav appears to use
        try {
            const rawUser = localStorage.getItem("user");
            if (rawUser) {
                try {
                    const parsedUser = JSON.parse(rawUser);
                    const candidate =
                        parsedUser?.name ||
                        parsedUser?.data?.name ||
                        parsedUser?.user?.name ||
                        parsedUser?.full_name ||
                        parsedUser?.username;
                    if (candidate) return titleCase(String(candidate));
                    // raw string fallback
                } catch {
                    if (rawUser.trim()) return titleCase(rawUser.trim());
                }
            }
        } catch {
            // ignore and continue to other keys
        }

        const checkStorage = (storage) => {
            const possibleKeys = ["userContext", "userInfo", "authUser", "authUserInfo", "currentUser", "profile", "userProfile"];
            for (const key of possibleKeys) {
                const raw = storage.getItem(key);
                if (!raw) continue;
                try {
                    const parsed = JSON.parse(raw);
                    const candidate =
                        parsed?.name ||
                        parsed?.full_name ||
                        parsed?.username ||
                        (parsed?.data && (parsed.data.name || parsed.data.full_name)) ||
                        (parsed?.user && (parsed.user.name || parsed.user.full_name)) ||
                        (parsed?.first_name && parsed?.last_name && `${parsed.first_name} ${parsed.last_name}`);
                    if (candidate) return titleCase(String(candidate));
                } catch {
                    if (raw && raw.trim()) return titleCase(raw.trim());
                }
            }
            return null;
        };

        return checkStorage(localStorage) || checkStorage(sessionStorage) || null;
    };

    // NEW: prefer logged-in user's display name for "Prepared By" (initialize from storage)
    const [preparedByName, setPreparedByName] = useState(() => getStoredUserName() || "");

    // Keep preparedByName in sync when other code updates localStorage (e.g., HeaderNav)
    useEffect(() => {
        const handleStorage = (event) => {
            if (!event) return;
            const watchedKeys = ["user", "userContext", "userInfo", "authUser", "authUserInfo", "currentUser", "profile", "userProfile"];
            if (watchedKeys.includes(event.key)) {
                setPreparedByName(getStoredUserName() || "");
            }
        };
        window.addEventListener("storage", handleStorage);
        // also refresh on mount in case HeaderNav updated after this component mounted
        setPreparedByName(getStoredUserName() || preparedByName || "");
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    // POLL: same-tab updates to localStorage (HeaderNav may set user after this component mounted)
    useEffect(() => {
        if (preparedByName) return;
        let attempts = 0;
        const maxAttempts = 10; // 10 * 500ms = 5s max
        const intervalId = setInterval(() => {
            const name = getStoredUserName();
            if (name) {
                setPreparedByName(name);
                clearInterval(intervalId);
                return;
            }
            attempts += 1;
            if (attempts >= maxAttempts) {
                clearInterval(intervalId);
            }
        }, 500);

        return () => clearInterval(intervalId);
    }, [preparedByName]);

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const handleMail = () => alert("Mail clicked!");
    const handlePrinter = () => window.print();
    const handleDownload = () => {
        const csvContent = [
            ["Ref", "Description", "Type", "Category", "Balance", "Status"],
            ...ledgerData.map(d => [d.ref, d.description, d.type, d.category, d.balance, d.status])
        ].map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "chart_of_accounts.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleSettings = () => navigate("/settings/chart-ledger");

    // helper to format date safely
    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    // helper to format money using existing formatMoney
    const formatCurrency = (value) => {
        return formatMoney(value);
    };

    // derive receipt-related values from payment with fallbacks
    const receiptNo = payment?.receipt_number || payment?.invoice_number || payment?.uuid || "N/A";
    const receiptDate = payment?.transaction_date || payment?.date_of_invoice || payment?.created_at || null;
    const receivedFrom = payment?.particular || payment?.supplier?.name || payment?.supplier || "N/A";
    const paymentMethod = payment?.mode?.name || (payment?.payment_mode
        ? (String(payment.payment_mode) === "1" ? "Bank Transfer" : String(payment.payment_mode) === "2" ? "Cheque" : `Method ${payment.payment_mode}`)
        : "N/A");
    const referenceNo = payment?.invoice_number || payment?.uuid || "N/A";

    // Use base currency (from company) first, then payment currency, then defaults
    const displayCurrencyCode = baseCurrencyCode || payment?.currency?.code || "NGN";
    const displayCurrencySymbol = baseCurrencySymbol || payment?.currency?.symbol || "₦";

    const lineAmountValue = parseFloat(payment?.total_currency_amount ?? payment?.currency_amount ?? payment?.amount ?? 0);
    const subtotalValue = lineAmountValue;
    const taxValue = 0; // API doesn't provide tax per receipt in sample; keep 0 unless you want to compute
    const totalValue = parseFloat(payment?.total_converted_amount ?? payment?.total_currency_amount ?? subtotalValue);

    // NEW: convert numbers to words (handles integer + two-decimal fractional part)
    const integerToWords = (n) => {
        if (n === 0) return "zero";
        const units = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
            "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
            "seventeen", "eighteen", "nineteen"];
        const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
        const scales = [
            { value: 1e12, name: "trillion" },
            { value: 1e9, name: "billion" },
            { value: 1e6, name: "million" },
            { value: 1e3, name: "thousand" },
            { value: 1e2, name: "hundred" }
        ];

        const words = [];

        const helper = (num) => {
            let str = "";
            if (num < 20) return units[num];
            if (num < 100) {
                const t = Math.floor(num / 10);
                const u = num % 10;
                return tens[t] + (u ? "-" + units[u] : "");
            }
            for (let i = 0; i < scales.length; i++) {
                const { value, name } = scales[i];
                if (num >= value) {
                    const count = Math.floor(num / value);
                    const remainder = num % value;
                    if (value === 100) {
                        str += helper(count) + " " + name;
                    } else {
                        str += helper(count) + " " + name;
                    }
                    if (remainder) str += " " + helper(remainder);
                    return str;
                }
            }
            return str;
        };

        return helper(n).trim();
    };

    const convertAmountToWords = (amount, currencyCode) => {
        const absAmount = Math.abs(Number(amount) || 0);
        const integerPart = Math.floor(absAmount);
        const fractionPart = Math.round((absAmount - integerPart) * 100); // two decimals

        // currency names and fractional unit names map (add more as needed)
        const currencyNameMap = {
            NGN: { name: "Naira", fraction: "kobo" },
            USD: { name: "Dollar", fraction: "cents" },
            EUR: { name: "Euro", fraction: "cents" },
            GBP: { name: "Pound", fraction: "pence" }
        };
        const currencyInfo = currencyNameMap[currencyCode] || { name: currencyCode || "Units", fraction: "cents" };

        const intWords = integerToWords(integerPart);
        let words = "";

        if (integerPart === 0 && fractionPart === 0) {
            words = `Zero ${currencyInfo.name}`;
        } else if (fractionPart === 0) {
            words = `${intWords} ${currencyInfo.name}`;
        } else if (integerPart === 0) {
            const fracWords = integerToWords(fractionPart);
            words = `${fracWords} ${currencyInfo.fraction} ${currencyInfo.name.startsWith(currencyCode) ? "" : ""}`.trim();
            // prefer "X kobo" when no integer part
        } else {
            const fracWords = integerToWords(fractionPart);
            words = `${intWords} ${currencyInfo.name} and ${fracWords} ${currencyInfo.fraction}`;
        }

        // Capitalize first letter and append "Only"
        return words.replace(/^\w/, c => c.toUpperCase()) + " Only";
    };

    const amountInWords = convertAmountToWords(totalValue, displayCurrencyCode);

    return (
        <div>
            <div className={`${classes.noPrint}`}>
                <HeaderNav activePage={pageMap[location.pathname]} />
            </div>

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns} onClick={() => navigate("/transactions/receivables/sales")}>
                        New Sales Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/inventory/purchaseOrder")}>
                        New Purchase Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/customer-sales")}>
                        All Invoices
                    </button>
                    <button className={`${classes.btns} ${classes.greens}`} onClick={() => navigate("/settings/product-services")}>
                        Product & Services
                    </button>
                </div>
                <div className={classes.act}>
                    <form className={classes.form}>
                        <div className={classes.group}>
                            <label>Period:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>Current month</option>
                                    <option>Last month</option>
                                    <option>This year</option>
                                    <option>Custom range</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>Group by:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>None</option> <option>Date</option>
                                    <option>Transaction Type</option>
                                    <option>Amount</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div> </div> <div className={classes.group1}>
                            <label>Start Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>01/01/25</option> <option>01/12/24</option>
                                    <option>01/11/24</option> <option>01/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>{new Date().toLocaleDateString('en-GB')}</option>
                                    <option>31/12/24</option> <option>30/11/24</option>
                                    <option>31/10/24</option> </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <button className={classes.greenBtn}
                                type="button"> <BarChart2 size={16} style={{ marginRight: "6px" }}
                                />
                                Run Report
                            </button>
                        </div>
                    </form>
                    <div className={classes.iconbarr}>
                        <div className={classes.iconbar}>
                            <Filter size={16} style={{ marginRight: "6px" }}
                            />
                            Filter
                        </div>
                        <div className={classes.iconbar}>
                            <CalendarClock size={16} style={{ marginRight: "6px" }} />
                            Schedule
                        </div>
                    </div>
                </div>
                <div className={classes.matron}> <div>
                    <button className={classes.backButton}
                        onClick={() => navigate(-1)}> <ArrowLeft size={16} style={{ marginRight: '6px' }}
                        />
                    </button>
                </div>
                    <div>
                        <p className={classes.toplea}>Payment Receipt</p>
                        <p className={classes.topl}>Payment as of {new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                <div className={classes.containere}>
                    <div className={classes.iconbars}>
                        <div className={classes.iconbarss}>
                            <div className={classes.iconbar} onClick={handleMail} style={{ cursor: "pointer" }}>
                                <Mail size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handlePrinter} style={{ cursor: "pointer" }}>
                                <Printer size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handleDownload} style={{ cursor: "pointer" }}>
                                <Download size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handleSettings} style={{ cursor: "pointer" }}>
                                <Settings size={16} />
                            </div>
                        </div>
                    </div>
                    {/* replaced static header with dynamic companyInfo */}
                    <header className={classes.pr_header}>
                        <div className={classes.pr_logo}>
                            {/* show company logo if available else fallback image path */}
                            <img
                                src={companyInfo.logo || "/company-logo.png"}
                                alt={`${companyInfo.name || "Company"} Logo`}
                                className={classes.pr_logoImg}
                            />
                        </div>
                        <div className={classes.pr_companyInfo}>
                            <h1 className={classes.pr_companyName}>{companyInfo.name || "Company Name"}</h1>
                            <p className={classes.pr_companyAddr}>{companyInfo.address || "Company address not provided"}</p>
                            <p className={classes.pr_companyContact}>
                                {companyInfo.email ? `Email: ${companyInfo.email}` : "Email: N/A"}
                                {companyInfo.phone ? ` | Tel: ${companyInfo.phone}` : ""}
                            </p>
                        </div>
                    </header>
                    { /* replaced content starts */}
                    {/* <div className={classes.pr_voucher}> */}
                        <div className={classes.pr_title}> PAYMENT VOUCHER</div>

                        <table className={classes.pr_table}>
                            <tbody>
                                <tr>
                                    <th className={classes.pr_label}>Voucher No:</th>
                                    <td className={classes.pr_value}>{receiptNo}</td>
                                    <th className={classes.pr_label}>Date:</th>
                                    <td className={classes.pr_value}>{formatDate(receiptDate)}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_label}>Payee:</th>
                                    <td className={classes.pr_value}>{receivedFrom}</td>
                                    <th className={classes.pr_label}>Payment Mode:</th>
                                    <td className={classes.pr_value}>{paymentMethod}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_label}>Bank Name:</th>
                                    <td className={classes.pr_value}>{payment?.supplier?.bank_name || payment?.payment_bank || "N/A"}</td>
                                    <th className={classes.pr_label}>Account No:</th>
                                    <td className={classes.pr_value}>{payment?.supplier?.account_number || payment?.account_number || "N/A"}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_label} >Cheque No:</th>
                                    <td className={classes.pr_value}>{payment?.teller_number || payment?.receipt_number || payment?.uuid || "N/A"}</td>
                                    <th className={classes.pr_label}>Cheque Date:</th>
                                    <td className={classes.pr_value}>{formatDate(payment?.date_logded || payment?.voucher_date || payment?.cheque_date)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className={classes.pr_sectionTitle}>Details of Payment</div>

                        <table className={classes.pr_table}>
                            <thead>
                                <tr>
                                    <th className={classes.pr_th}>#</th>
                                    <th className={classes.pr_th}>Description / Particulars</th>
                                    <th className={classes.pr_th} style={{ textAlign: "right" }}>Amount ({displayCurrencyCode})</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={classes.pr_td}>1</td>
                                    <td className={classes.pr_td}>{payment?.description || payment?.narration || "Payment"}</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>{displayCurrencySymbol}{formatCurrency(lineAmountValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>Subtotal</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>{displayCurrencySymbol}{formatCurrency(subtotalValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>VAT (7.5%)</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>{displayCurrencySymbol}{formatCurrency(taxValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>Total Amount</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 800 }}>{displayCurrencySymbol}{formatCurrency(totalValue)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table className={classes.pr_summary}>
                            <tbody>
                                <tr>
                                    <td className={`${classes.pr_summaryLabel}`}>Amount in Words:</td>
                                    <td className={classes.pr_summaryValue}>{amountInWords || "N/A"}</td>
                                </tr>
                                <tr>
                                    <td className={`${classes.pr_summaryLabel}`}>Prepared By:</td>
                                    <td className={classes.pr_summaryValue}>{preparedByName || "N/A"}</td>
                                </tr>
                                <tr>
                                    <td className={`${classes.pr_summaryLabel}`}>Checked By:</td>
                                    <td className={classes.pr_summaryValue}>{payment?.checked_by || "N/A"}</td>
                                </tr>
                                <tr>
                                    <td className={`${classes.pr_summaryLabel}`}>Approved By:</td>
                                    <td className={classes.pr_summaryValue}>{payment?.approved_by || "N/A"}</td>
                                </tr>
                                <tr>
                                    <td className={`${classes.pr_summaryLabel}`}>Narration:</td>
                                    <td className={classes.pr_summaryValue}>{payment?.narration || payment?.description || "N/A"}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className={classes.pr_chequeSection}>
                            <h4 className={classes.pr_chequeTitle}>Cheque Details</h4>
                            <table className={classes.pr_table}>
                                <tbody>
                                    <tr>
                                        <th className={classes.pr_label} style={{textAlign: 'right'}}>Cheque No</th>
                                        <td className={classes.pr_value}>{payment?.teller_number || payment?.receipt_number || "N/A"}</td>
                                        <th className={classes.pr_label} style={{textAlign: 'right'}}>Bank</th>
                                        <td className={classes.pr_value}>{payment?.supplier?.bank_name || payment?.payment_bank || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th className={classes.pr_label} style={{textAlign: 'right'}}>Payee</th>
                                        <td className={classes.pr_value}>{receivedFrom}</td>
                                        <th className={classes.pr_label} style={{textAlign: 'right'}}>Amount ({displayCurrencyCode})</th>
                                        <td className={classes.pr_value}>{displayCurrencySymbol}{formatCurrency(totalValue)}</td>
                                    </tr>
                                    <tr>
                                        <th className={classes.pr_label} style={{textAlign: 'right'}}>Date</th>
                                        <td className={classes.pr_value}>{formatDate(payment?.date_logded || payment?.cheque_date || payment?.transaction_date)}</td>
                                        <th className={classes.pr_label} style={{textAlign: 'right'}}>Signature</th>
                                        <td className={classes.pr_value}>_________________________</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className={classes.pr_signatures}>
                            <div className={classes.pr_signBoxprpby}>
                                <div className={classes.dtresp}>{displayName}</div>
                                <div className={classes.pr_signBox}>Prepared By</div>
                            </div>
                            
                            <div className={classes.pr_signBox}>Checked By</div>
                            <div className={classes.pr_signBox}>Approved By</div>
                        </div>

                        <div className={classes.pr_footer}>
                            <p className={classes.pr_note}><strong>Note:</strong> Attach the original cheque to this voucher before submission to Accounts for processing.</p>
                            <p className={classes.pr_generated}>Generated by Chequestream Accounting System © {new Date().getFullYear()}</p>
                        </div>
                    {/* </div> */}
                    { /* replaced content ends */}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default PaymentVoucher;
